/**
 * Реклама AdsGram.
 *
 * Скрипт подгружается лениво, при первом показе, а не тегом в `<head>`, как
 * советует их документация. Причина: у нас реклама видна далеко не всем —
 * подписчику не показывается вовсе, а вне Telegram её и показать негде. Грузить
 * чужой скрипт каждому при открытии приложения ради меньшинства не стоит ни
 * трафика, ни лишнего стороннего кода на странице.
 *
 * Идентификаторы блоков — в `shared/ads.ts`.
 *
 * Награду за досмотр начисляет НЕ этот код: её подтверждает сервер AdsGram
 * обратным вызовом на `/adsgram/reward`. Здесь мы лишь узнаём, что ролик
 * досмотрен, и идём спрашивать сервер.
 */
import { AD_BLOCK_INTERSTITIAL, AD_BLOCK_REWARD } from '../../../shared/ads.ts'
import { tg } from './telegram'

const SDK_URL = 'https://sad.adsgram.ai/js/sad.min.js'

/** Что возвращает их `show()`. Форма — из их документации. */
interface ShowResult {
  done: boolean
  description: string
  state: 'load' | 'render' | 'playing' | 'destroy'
  error: boolean
}

interface AdController {
  show: () => Promise<ShowResult>
}

declare global {
  interface Window {
    Adsgram?: { init: (opts: { blockId: string }) => AdController }
  }
}

let loading: Promise<boolean> | null = null

/** Подгружает SDK один раз. `false` — не удалось: сеть, блокировщик, что угодно. */
function loadSdk(): Promise<boolean> {
  if (window.Adsgram) return Promise.resolve(true)
  if (loading) return loading

  loading = new Promise<boolean>((resolve) => {
    const el = document.createElement('script')
    el.src = SDK_URL
    el.async = true
    el.onload = () => resolve(Boolean(window.Adsgram))
    // Блокировщики рекламы существуют, и это нормально: показа не будет, но
    // приложение обязано работать дальше как ни в чём не бывало.
    el.onerror = () => resolve(false)
    document.head.appendChild(el)
  })
  return loading
}

/** Контроллеры кешируем: инициализация блока нужна один раз. */
const controllers = new Map<string, AdController>()

async function controllerFor(blockId: string): Promise<AdController | null> {
  if (!(await loadSdk())) return null
  const cached = controllers.get(blockId)
  if (cached) return cached
  try {
    const made = window.Adsgram!.init({ blockId })
    controllers.set(blockId, made)
    return made
  } catch {
    return null
  }
}

/** Реклама вообще возможна только внутри Telegram: снаружи её негде показать. */
export const adsAvailable = (): boolean => Boolean(tg)

/**
 * Ролик за награду.
 *
 * `true` — человек досмотрел до конца, и сервер AdsGram уже дёрнул наш
 * обратный вызов (или вот-вот дёрнет: он идёт server-to-server и может
 * отстать на секунду-другую). `false` — не досмотрел, отказался или показа
 * не случилось.
 */
export async function showRewardAd(): Promise<boolean> {
  const ctrl = await controllerFor(AD_BLOCK_REWARD)
  if (!ctrl) return false
  try {
    const res = await ctrl.show()
    return res.done === true
  } catch {
    // Их промис отклоняется, если ролик не досмотрен. Это не ошибка нашего
    // кода и не повод что-то показывать человеку.
    return false
  }
}

/**
 * Полноэкранный показ. Результат не важен: наградой он не оборачивается, и
 * закрытый на середине ролик для нас ровно то же, что досмотренный.
 */
export async function showInterstitial(): Promise<void> {
  const ctrl = await controllerFor(AD_BLOCK_INTERSTITIAL)
  if (!ctrl) return
  try {
    await ctrl.show()
  } catch {
    /* пропущено или не показалось — идём дальше */
  }
}
